import { queryClient } from "Utils/queryClient";
import { InfiniteData, useMutation } from "@tanstack/react-query";
import { NotificationApiV2 } from "Data/Notification.api.v2";
import {
  INotification,
  INotificationInAppObra,
  INotificationInAppObraCount,
  INotificationInAppTopic,
  INotificationInfinityData,
  INotificationReadRequest
} from "Data/interfaces/Notification/INotification";
import {
  NOTIFICATIONS_PAGE_QUERY_KEY,
  NOTIFICATIONS_COUNT_QUERY_KEY,
  NOTIFICATIONS_QUERY_KEY,
  NOTIFICATIONS_TEMPORARY_QUERY_KEY,
  NOTIFICATIONS_IN_APP_OBRA_QUERY_KEY,
  NOTIFICATIONS_IN_APP_OBRA_COUNT_QUERY_KEY,
  NOTIFICATIONS_IN_APP_TOPIC_QUERY_KEY,
  NOTIFICATIONS_PERSONAL_KEY,
} from "Data/keys/QueryKeys";
import { Mixpanel } from "Utils/MixPanel";
import { useSelector } from "react-redux";
import { getUserInfo } from "Store/Auth/Auth.selector";
import { getCurrentTenant, getTenants } from "Store/Tenant/Tenant.selector";
import { IReadNotificationTracking } from "Data/interfaces/Notification/IReadNotificationTrackingRequest";

export const useQueryReadNotification = () => {
  const userInfo = useSelector(getUserInfo);
  const currentTenant = useSelector(getCurrentTenant);
  const tenants = useSelector(getTenants);

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: (variables: INotificationReadRequest) => {
      return NotificationApiV2.readNotification(variables.notificationId);
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries<InfiniteData<INotificationInfinityData>>(
          { queryKey: [NOTIFICATIONS_QUERY_KEY] }
        ),
        queryClient.invalidateQueries<InfiniteData<INotificationInfinityData>>(
          { queryKey: [NOTIFICATIONS_QUERY_KEY, Number(variables.constructionSiteId)] }
        ),
        queryClient.invalidateQueries<INotification[]>(
          { queryKey: [NOTIFICATIONS_TEMPORARY_QUERY_KEY] }
        ),
        queryClient.invalidateQueries<INotification[]>(
          { queryKey: [NOTIFICATIONS_TEMPORARY_QUERY_KEY, Number(variables.constructionSiteId)] }
        ),
        queryClient.invalidateQueries<number>(
          { queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY] }
        ),
        queryClient.invalidateQueries<number>(
          { queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY, Number(variables.constructionSiteId)] }
        ),
        queryClient.invalidateQueries<InfiniteData<INotificationInfinityData>>(
          { queryKey: [NOTIFICATIONS_PAGE_QUERY_KEY, variables?.filters ?? {}] }
        ),
        queryClient.invalidateQueries<INotificationInAppObraCount[]>(
          { queryKey: [NOTIFICATIONS_IN_APP_OBRA_COUNT_QUERY_KEY] }
        ),
        queryClient.invalidateQueries<INotificationInAppObra[]>(
          { queryKey: [NOTIFICATIONS_IN_APP_OBRA_QUERY_KEY, Number(variables.constructionSiteId)] }
        ),
        queryClient.invalidateQueries<INotificationInAppTopic[]>(
          { queryKey: [NOTIFICATIONS_IN_APP_TOPIC_QUERY_KEY, Number(variables.constructionSiteId)] }
        ),
        queryClient.invalidateQueries<InfiniteData<INotificationInfinityData>>(
          { queryKey: [NOTIFICATIONS_QUERY_KEY, NOTIFICATIONS_PERSONAL_KEY] }
        ),
        queryClient.invalidateQueries<number>(
          { queryKey: [NOTIFICATIONS_COUNT_QUERY_KEY, NOTIFICATIONS_PERSONAL_KEY] }
        ),
        queryClient.invalidateQueries<INotification[]>(
          { queryKey: [NOTIFICATIONS_TEMPORARY_QUERY_KEY, NOTIFICATIONS_PERSONAL_KEY] }
        ),
      ]);

      Mixpanel.track({
        name: 'READ_NOTIFICATION',
        props: {
          folderId: variables.itemAuxId,
          constructionSiteId: variables.constructionSiteId,
          quantity: 1,
        } as IReadNotificationTracking,
        userInfo: userInfo,
        currentListTenant: currentTenant,
        tenantIds: tenants && tenants.length > 0
          ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
          : undefined,
      })
    },
  });

  return {
    readNotification: mutate,
    readNotificationAsync: mutateAsync,
    isLoading: isPending,
  };
};